import { Pagination } from "services/search";
import {
  DamageStatus,
  FleetVehicleDamage,
  FleetVehicleDamageEditFormValues,
  FleetVehicleDamageRow,
  FleetVehicleDamageSearchFormValues,
} from "./types";
import { DataWithPagination } from "services/api/types";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import {
  transformFleetVehicleDamage,
  transformFleetVehicleDamageOnCreate,
  transformFleetVehicleDamageOnUpdate,
  transformFleetVehicleDamageRow,
  transformDamageStatus,
} from "./transformations";
import { FleetDocumentUpload } from "../documents/types";
import { transformErrorResponse } from "services/api/transformation";
import {
  DropdownCacheKey,
  invalidateDropdownCache,
} from "services/hooks/useDropdownCache";

/*---------------------------------------------------------------------------------------
  STATUSES
-----------------------------------------------------------------------------------------*/
export const getDamageStatusesAPI = async (): Promise<
  DamageStatus[]
> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/damages/statuses`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data.map(transformDamageStatus);
};

/*---------------------------------------------------------------------------------------
  SEARCH
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleDamagesAPI = async (
  pagination: Pagination,
  filters: FleetVehicleDamageSearchFormValues
): Promise<DataWithPagination<FleetVehicleDamageRow>> => {
  let path = `/v1/fleet/vehicles/damages`;

  // Bez `vehicleId` endpoint vraća štete cijelog tenanta — to koristi zajednički pregled.
  const params: string[] = [];
  if (filters.vehicleId) params.push(`vehicleId=${filters.vehicleId}`);
  if (filters.vehicleDamageStatusId)
    params.push(`vehicleDamageStatusId=${filters.vehicleDamageStatusId}`);
  if (filters.search) params.push(`name=${encodeURIComponent(filters.search)}`);
  if (filters.vehicleManufacturerId)
    params.push(`vehicleManufacturerId=${filters.vehicleManufacturerId}`);
  if (filters.vehicleModelId) params.push(`vehicleModelId=${filters.vehicleModelId}`);
  if (filters.vehicleTypeId) params.push(`vehicleTypeId=${filters.vehicleTypeId}`);
  if (filters.damageDateFrom)
    params.push(`damageDateFrom=${filters.damageDateFrom.toISOString()}`);
  if (filters.damageDateTo)
    params.push(`damageDateTo=${filters.damageDateTo.toISOString()}`);
  if (params.length > 0) path += `?${params.join("&")}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleDamageRow
  );
};

/*---------------------------------------------------------------------------------------
  INFO
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleDamageAPI = async (
  id: string
): Promise<FleetVehicleDamage> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/damages/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleDamage(response.data);
};

/*---------------------------------------------------------------------------------------
  CREATE
-----------------------------------------------------------------------------------------*/
export const createFleetVehicleDamageAPI = async (
  vehicleId: string,
  formValues: FleetVehicleDamageEditFormValues,
  photoUploads: FleetDocumentUpload[],
  documentUploads: FleetDocumentUpload[],
  t: (key: string) => string
): Promise<FleetVehicleDamage> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/vehicles/damages/",
      method: "POST",
      body: transformFleetVehicleDamageOnCreate(
        vehicleId,
        formValues,
        photoUploads,
        documentUploads
      ),
    },
    {
      getMessage: {
        success: () =>
          t("admin.fleet.vehicle.damage.create.message.success"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  // Nova šteta se odmah bira u statusnom modalu, pa keširani popis mora otpasti.
  invalidateDropdownCache(DropdownCacheKey.VehicleDamages);

  return transformFleetVehicleDamage(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleDamageAPI = async (
  damageId: string,
  formValues: FleetVehicleDamageEditFormValues,
  photoUploads: FleetDocumentUpload[],
  documentUploads: FleetDocumentUpload[],
  t: (key: string) => string
): Promise<FleetVehicleDamage> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/damages/${damageId}`,
      method: "PUT",
      body: transformFleetVehicleDamageOnUpdate(
        formValues,
        photoUploads,
        documentUploads
      ),
    },
    {
      getMessage: {
        success: () =>
          t("admin.fleet.vehicle.damage.update.message.success"),
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.VehicleDamages);

  return transformFleetVehicleDamage(response.data);
};

/*---------------------------------------------------------------------------------------
  DELETE
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleDamageAPI = async (
  id: string,
  t: (key: string) => string
): Promise<FleetVehicleDamage> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/damages/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () =>
          t("admin.fleet.vehicle.damage.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};
